// LiveStreamChannelsHealthChecker.jsx
import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { JWT_SCOPE_LIVESTREAM_MODERATOR } from '../RemoteConfigKeys.js';

import {
  LIVESTREAM_CHANNEL_HEALTH_CHECK_INTERVAL_MSEC,
  LIVESTREAM_CHANNEL_MAX_RESUBSCRIBE_ATTEMPTS,
} from '../resource/liveStreamConstants.js';
import { getExpectedLivestreamChannelInfos } from '../resource/livestreamUtils.js';
import ServiceWorkerMessenger from '../resource/ServiceWorkerMessenger.js';
import { sendMessageToSW } from '../serviceWorker/helpers.js';
import { pusher as pusherDebug } from '../resource/debug.js';
import { Game } from '../resource/gameConstants.js';

import getGoalData from '../selector/getGoalData.js';
import getIsPermissionMatched from '../selector/getIsPermissionMatched.js';
import getLiveStreamData from '../selector/getLiveStreamData.js';
import getMeData from '../selector/getMeData.js';
import getOperationData from '../selector/getOperationData.js';

const pusherLog = pusherDebug.extend('log:livestreamChannelHealth');
const pusherErrorLog = pusherDebug.extend('error:livestreamChannelHealth');

const HEALTH_CHECK_APPLY_TIMEOUT_MSEC = 5000;

/**
 * Periodic Pusher channel health check for the LiveStreams page.
 * Polls every `LIVESTREAM_CHANNEL_HEALTH_CHECK_INTERVAL_MSEC`, asks the SW
 * via `ServiceWorkerMessenger.apply(['getLivestreamChannelsHealth'])` which
 * livestream channels are actually subscribed, and resubscribes any unhealthy
 * channel through the info's SW message (capped per channel per mount
 * by `LIVESTREAM_CHANNEL_MAX_RESUBSCRIBE_ATTEMPTS`).
 *
 * Per-channel resubscribe attempts are kept in a component-local `useRef`,
 * so leaving the livestream (unmount) or switching `activeStreamId`
 * naturally resets the counter — no Redux cleanup needed.
 *
 * Renders nothing.
 *
 * @example
 * <LiveStreamChannelsHealthChecker activeStreamId={activeStreamId} />
 */
export const LiveStreamChannelsHealthChecker = ({ activeStreamId = null }) => {
  const meId = useSelector(state => getMeData(state, 'id'));
  const exclusivePrivateStatus = useSelector(state =>
    activeStreamId
      ? getLiveStreamData(state, [activeStreamId, 'exclusivePrivate'], 'status')
      : null
  );
  const currentExclusivePrivateId = useSelector(state =>
    activeStreamId
      ? getLiveStreamData(
          state,
          [activeStreamId, 'exclusivePrivate'],
          'currentId'
        )
      : null
  );
  const exclusivePrivateBuyerId = useSelector(state =>
    currentExclusivePrivateId
      ? getGoalData(
          state,
          [currentExclusivePrivateId, 'context'],
          'exclusive_to'
        )
      : null
  );
  const isStreamIdLiveStreaming = useSelector(state =>
    activeStreamId
      ? getLiveStreamData(state, [activeStreamId], 'isLiveStreaming')
      : false
  );
  const usingGameId = useSelector(state =>
    getOperationData(state, ['game'], 'usingGameId')
  );
  const isGameOpen = useSelector(state =>
    getOperationData(state, ['liveStream', 'panel'], 'isGameOpen')
  );
  const isLivestreamModerator = useSelector(
    state => !!getIsPermissionMatched(state, JWT_SCOPE_LIVESTREAM_MODERATOR)
  );

  // Latest props snapshot; tick reads from here so non-activeStreamId props
  // updates don't restart the interval.
  const propsRef = useRef(null);
  propsRef.current = {
    activeStreamId,
    streamerUserId: activeStreamId,
    meId,
    shouldSubscribeStreamChannel: isStreamIdLiveStreaming !== false,
    exclusivePrivateStatus,
    isExclusivePrivateBuyer: meId != null && meId === exclusivePrivateBuyerId,
    isLivestreamModerator,
    isSicBoCampaignActive: Game.SIC_BO === usingGameId && isGameOpen === true,
  };
  // { [channelName]: attempts } — resets to {} on activeStreamId change.
  const attemptsRef = useRef({});

  useEffect(() => {
    if (!activeStreamId) return;
    attemptsRef.current = {};

    const tick = async () => {
      const props = propsRef.current;
      if (!props?.activeStreamId) return;
      const infos = getExpectedLivestreamChannelInfos(props);
      if (infos.length === 0) return;

      const messenger = ServiceWorkerMessenger.getInstance();
      if (!messenger) {
        pusherErrorLog('ServiceWorkerMessenger unavailable; skipping tick');
        return;
      }

      let channelStates;
      try {
        channelStates = await messenger.apply({
          selectPath: ['getLivestreamChannelsHealth'],
          args: [
            {
              channelNames: infos.map(({ channelName }) => channelName),
            },
          ],
          options: {
            timeoutMsec: HEALTH_CHECK_APPLY_TIMEOUT_MSEC,
            objectDepth: 2,
          },
        });
      } catch (error) {
        pusherErrorLog('getLivestreamChannelsHealth apply failed', error);
        return;
      }

      infos.forEach(info => {
        const { channelName } = info;
        const channelData = channelStates?.[channelName];
        const subscribed = channelData?.subscribed === true;
        const attempts = attemptsRef.current[channelName] || 0;

        if (subscribed) {
          if (attempts !== 0) attemptsRef.current[channelName] = 0;
          return;
        }

        if (attempts >= LIVESTREAM_CHANNEL_MAX_RESUBSCRIBE_ATTEMPTS) {
          pusherErrorLog(
            'reached resubscribe limit for livestream channel',
            channelName
          );
          return;
        }

        pusherLog('resubscribing unhealthy livestream channel', channelName);
        sendMessageToSW({
          type: info.subscribeMessageType,
          payload: info.subscribePayload,
        });
        attemptsRef.current[channelName] = attempts + 1;
      });
    };

    // Intentionally NO immediate tick(): LiveStreamPusherHandler is still
    // dispatching its subscribe actions when this effect runs, so any check
    // at t=0 would observe in-flight channels and falsely report them
    // unhealthy. Health-check is a 30s-cadence watchdog, not a primer.
    const intervalId = setInterval(
      tick,
      LIVESTREAM_CHANNEL_HEALTH_CHECK_INTERVAL_MSEC
    );
    return () => clearInterval(intervalId);
  }, [activeStreamId]);

  return null;
};

LiveStreamChannelsHealthChecker.propTypes = {
  activeStreamId: PropTypes.string,
};

export default LiveStreamChannelsHealthChecker;
